/**
 * Terpenes Supabase API Client
 * Part of Story 1.4: Build Terpene Browser Modal
 * 
 * Provides typed queries for terpenes data from Supabase
 * with support for search, filtering, and sorting.
 */

import { supabase } from '@/lib/supabase/client'
import type { TerpeneRow } from '@/types/supabase'

export interface TerpeneFilters {
  search?: string
  category?: string
  effects?: string[]
  sources?: string[]
  sort?: 'name' | 'category' | 'created_at'
  limit?: number
  offset?: number
}

export const terpeneApi = {
  /**
   * Get all terpenes with optional filters
   */
  async list(filters?: TerpeneFilters) {
    if (!supabase) {
      console.error('[TerpeneAPI] Supabase client not initialized')
      throw new Error('Supabase client not initialized')
    }

    let query = supabase.from('terpenes').select('*')

    // Apply search filter
    if (filters?.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
      )
    }

    // Apply category filter
    if (filters?.category) {
      query = query.eq('category', filters.category)
    }

    // Apply effects filter (array contains)
    if (filters?.effects && filters.effects.length > 0) {
      // For multiple effects, use OR logic
      const effectsFilter = filters.effects
        .map(effect => `primary_effects.cs.{${effect}}`)
        .join(',')
      query = query.or(effectsFilter)
    }

    // Apply sources filter (array contains)
    if (filters?.sources && filters.sources.length > 0) {
      // For multiple sources, use OR logic
      const sourcesFilter = filters.sources
        .map(source => `natural_sources.cs.{${source}}`)
        .join(',')
      query = query.or(sourcesFilter)
    }

    // Apply sorting
    const sortField = filters?.sort || 'name'
    query = query.order(sortField, { ascending: true })

    // Apply pagination
    if (filters?.limit) {
      query = query.limit(filters.limit)
    }
    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1)
    }

    const { data, error } = await query

    if (error) {
      console.error('[TerpeneAPI] Error fetching terpenes:', error)
      throw error
    }

    return data || []
  },

  /**
   * Get a single terpene by ID
   */
  async getById(id: string): Promise<TerpeneRow | null> {
    if (!supabase) {
      console.error('[TerpeneAPI] Supabase client not initialized')
      throw new Error('Supabase client not initialized')
    }

    const { data, error } = await supabase
      .from('terpenes')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('[TerpeneAPI] Error fetching terpene:', error)
      throw error
    }

    return data
  },

  /**
   * Get a single terpene by slug
   */
  async getBySlug(slug: string): Promise<TerpeneRow | null> {
    if (!supabase) {
      console.error('[TerpeneAPI] Supabase client not initialized')
      throw new Error('Supabase client not initialized')
    }

    const { data, error } = await supabase
      .from('terpenes')
      .select('*')
      .eq('slug', slug)
      .single()

    if (error) {
      console.error('[TerpeneAPI] Error fetching terpene by slug:', error)
      throw error
    }

    return data
  },

  /**
   * Get unique categories
   */
  async getCategories(): Promise<string[]> {
    const defaultCategories = ['Monoterpene', 'Sesquiterpene', 'Diterpene', 'Triterpene']
    
    if (!supabase) {
      console.warn('[TerpeneAPI] Supabase client not initialized, returning default categories')
      return defaultCategories
    }

    try {
      // Fetch all terpenes and extract unique categories client-side
      // This is more reliable than trying to get distinct values from Supabase
      const { data, error } = await supabase
        .from('terpenes')
        .select('category')
        .limit(100) // Limit to prevent huge queries

      if (error) {
        console.warn('[TerpeneAPI] Error fetching categories, using defaults:', error.message || 'Unknown error')
        return defaultCategories
      }

      if (!data || data.length === 0) {
        console.warn('[TerpeneAPI] No terpenes found, using default categories')
        return defaultCategories
      }

      // Extract unique categories, filtering out null/undefined
      const uniqueCategories = [...new Set(
        data
          .map(item => item.category)
          .filter(cat => cat !== null && cat !== undefined && cat !== '')
      )] as string[]

      // Sort alphabetically
      uniqueCategories.sort()

      return uniqueCategories.length > 0 ? uniqueCategories : defaultCategories
    } catch (err) {
      console.error('[TerpeneAPI] Unexpected error fetching categories:', err)
      return defaultCategories
    }
  },

  /**
   * Get all unique effects
   */
  async getEffects(): Promise<string[]> {
    const defaultEffects = [
      'Anti-inflammatory',
      'Analgesic',
      'Anxiolytic',
      'Sedative',
      'Energizing',
      'Focus',
      'Relaxation',
      'Appetite Stimulant',
      'Anti-nausea',
      'Mood Enhancement'
    ]

    if (!supabase) {
      console.warn('[TerpeneAPI] Supabase client not initialized, returning default effects')
      return defaultEffects
    }

    try {
      const { data, error } = await supabase
        .from('terpenes')
        .select('primary_effects')

      if (error) {
        console.error('[TerpeneAPI] Error fetching effects:', error)
        return defaultEffects
      }

      // Extract and flatten unique effects
      const allEffects = data?.flatMap(item => item.primary_effects || []) || []
      const uniqueEffects = [...new Set(allEffects)].sort()
      return uniqueEffects.length > 0 ? uniqueEffects : defaultEffects
    } catch (err) {
      console.error('[TerpeneAPI] Unexpected error fetching effects:', err)
      return defaultEffects
    }
  },

  /**
   * Get all unique natural sources
   */
  async getSources(): Promise<string[]> {
    const defaultSources = [
      'Cannabis',
      'Citrus Fruits',
      'Pine',
      'Lavender',
      'Black Pepper',
      'Hops',
      'Mango',
      'Lemongrass',
      'Eucalyptus',
      'Cloves'
    ]

    if (!supabase) {
      console.warn('[TerpeneAPI] Supabase client not initialized, returning default sources')
      return defaultSources
    }

    try {
      const { data, error } = await supabase
        .from('terpenes')
        .select('natural_sources')

      if (error) {
        console.error('[TerpeneAPI] Error fetching sources:', error)
        return defaultSources
      }

      // Extract and flatten unique sources
      const allSources = data?.flatMap(item => item.natural_sources || []) || []
      const uniqueSources = [...new Set(allSources)].sort()
      return uniqueSources.length > 0 ? uniqueSources : defaultSources
    } catch (err) {
      console.error('[TerpeneAPI] Unexpected error fetching sources:', err)
      return defaultSources
    }
  },

  /**
   * Search terpenes using full-text search
   */
  async search(query: string, limit = 10): Promise<TerpeneRow[]> {
    if (!supabase) {
      console.error('[TerpeneAPI] Supabase client not initialized')
      throw new Error('Supabase client not initialized')
    }

    const { data, error } = await supabase
      .rpc('search_terpenes', {
        search_query: query,
        limit_count: limit
      })

    if (error) {
      console.error('[TerpeneAPI] Error searching terpenes:', error)
      throw error
    }

    return data || []
  }
}